import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import "./index.css";
import "./styles/mobile-ios26.css"; // 📱 iOS 26 optimizations
import "./i18n";
import { NotificationProvider } from "./contexts/NotificationContext";
import { HelmetProvider } from "react-helmet-async";
// 🔥 CRITICAL: Ensure Firebase is initialized before any other code
import "./config/firebase";

// 🚀 CRITICAL: Handle chunk loading failures
window.addEventListener(
  "error",
  (event) => {
    // Check if it's a chunk loading error
    if (
      event.message?.includes("Loading chunk") ||
      event.message?.includes("Failed to fetch") ||
      event.message?.includes("Importing a module script failed")
    ) {
      const reloadKey = "chunk_reload_attempt";
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Only reload once every 5 seconds to prevent infinite loops
      if (!lastReload || now - parseInt(lastReload) > 5000) {
        console.warn("Chunk loading error detected, reloading page...");
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
      }
    }
  },
  true
);

// Componente para limpiar errores después de funcionamiento exitoso
const ErrorCleanup = () => {
  useEffect(() => {
    const cleanupTimer = setTimeout(() => {
      const currentReloads = sessionStorage.getItem("errorBoundaryReloads");
      if (currentReloads && parseInt(currentReloads, 10) > 0) {
     
        sessionStorage.removeItem("errorBoundaryReloads");
      }
    }, 30000);

    return () => clearTimeout(cleanupTimer);
  }, []);

  return null;
};

const AppWrapper = () => (
  <ErrorBoundary>
    <Suspense fallback={null}>
      <HelmetProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <NotificationProvider>
            <ErrorCleanup />
            <Toaster
              position="top-right"
              containerStyle={{
                zIndex: 99999,
              }}
              toastOptions={{
                style: {
                  background: "#FFFFFF",
                  color: "#1F2937",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  border: "2px solid #E5E7EB",
                  maxWidth: "400px",
                  wordWrap: "break-word",
                  zIndex: 99999,
                },
                success: {
                  style: {
                    background: "#FFFFFF",
                    color: "#065F46",
                    borderLeft: "6px solid #10B981",
                    border: "2px solid #D1FAE5",
                    fontWeight: "600",
                  },
                },
                error: {
                  style: {
                    background: "#FFFFFF",
                    color: "#991B1B",
                    borderLeft: "6px solid #EF4444",
                    border: "2px solid #FEE2E2",
                    fontWeight: "600",
                  },
                },
                duration: 6000,
              }}
            />
            <App />
          </NotificationProvider>
        </BrowserRouter>
      </HelmetProvider>
    </Suspense>
  </ErrorBoundary>
);

// Solo usar StrictMode en producción para evitar renders dobles durante desarrollo
const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

if (import.meta.env.PROD) {
  root.render(
    <React.StrictMode>
      <AppWrapper />
    </React.StrictMode>
  );
} else {
  root.render(<AppWrapper />);
}
